import type { Settings } from 'api/User/types'
import { Box } from 'components'
import React from 'react'
import { withTranslation } from 'react-i18next'
import type { Translate } from 'types'
import type { StoreDetails } from 'types/Store'

import { ClubCardTab, GeneralTab, LastDataTab, ReplenishmentTab, ResultTab, TrafficTab } from './StoreTabs'

enum DETAILS_TYPE {
  GENERAL = 'GENERAL',
  TRAFFIC = 'TRAFFIC',
  RESULT = 'RESULT',
  CLUBCARD = 'CLUBCARD',
  REPLENISHMENT = 'REPLENISHMENT',
  LAST_DATA = 'LAST_DATA',
}

interface Props {
  countryId: number
  details: StoreDetails
  exposTimestamp?: string
  isSisterStore?: boolean
  resaTimestamp?: string
  trafficTimestamp?: string
  userPrivileges: Settings
  t: Translate
}

interface State {
  type: DETAILS_TYPE
}

class StoreDetailsInfo extends React.Component<Props, State> {
  state = {
    type: DETAILS_TYPE.GENERAL,
  }

  boxTitles = () => {
    const { details, t: translate, userPrivileges } = this.props
    const showStoreReplenishment =
      userPrivileges.replenishmentVisibility &&
      details.storeReplenishment &&
      details.storeReplenishment.some(
        ({ replenishmentOnHandOthersCurrent, replenishmentOnHandShoesCurrent }) =>
          replenishmentOnHandOthersCurrent || replenishmentOnHandShoesCurrent,
      )
    const hasHistoryTraffic = details.trafficHistory && details.trafficHistory.some(({ trafficCurrent }) => trafficCurrent)

    return [
      {
        title: translate('revenue.details.general'),
        type: DETAILS_TYPE.GENERAL,
      },
      ...(details.results
        ? [
            {
              title: translate('revenue.details.result'),
              type: DETAILS_TYPE.RESULT,
              icon: <i className="fm fm-screenrotate" />,
            },
          ]
        : []),
      ...(hasHistoryTraffic
        ? [
            {
              title: translate('revenue.details.traffic'),
              type: DETAILS_TYPE.TRAFFIC,
            },
          ]
        : []),
      ...(details.revenueClubCurrent
        ? [
            {
              title: translate('revenue.details.clubCard'),
              type: DETAILS_TYPE.CLUBCARD,
            },
          ]
        : []),
      ...(showStoreReplenishment
        ? [
            {
              title: translate('revenue.details.replenishment'),
              type: DETAILS_TYPE.REPLENISHMENT,
            },
          ]
        : []),
      ...(this.showLastData
        ? [
            {
              title: translate('revenue.details.lastData'),
              type: DETAILS_TYPE.LAST_DATA,
            },
          ]
        : []),
    ]
  }

  get showLastData() {
    const { props } = this

    return !props.isSisterStore && Boolean(props.exposTimestamp || props.resaTimestamp || props.trafficTimestamp)
  }

  updateType = (newType: DETAILS_TYPE) => this.setState({ type: newType })

  renderContent = () => {
    const { countryId, details, exposTimestamp, isSisterStore, resaTimestamp, trafficTimestamp } = this.props
    const { dateCurrent, results, storeReplenishment, trafficCompared, trafficCurrent, trafficHistory } = details as any

    switch (this.state.type) {
      case DETAILS_TYPE.GENERAL:
        return <GeneralTab countryId={countryId} dateCurrent={dateCurrent} details={details} isSisterStore={isSisterStore} />

      case DETAILS_TYPE.TRAFFIC:
        return <TrafficTab trafficCurrent={trafficCurrent} trafficCompared={trafficCompared} trafficHistory={trafficHistory} />

      case DETAILS_TYPE.RESULT:
        return <ResultTab results={results} />

      case DETAILS_TYPE.CLUBCARD:
        return <ClubCardTab store={details} dateCurrent={dateCurrent} />

      case DETAILS_TYPE.REPLENISHMENT:
        return (
          <ReplenishmentTab
            dateCurrent={dateCurrent}
            replenishmentOnHandShoesCurrent={details.replenishmentOnHandShoesCurrent as any}
            replenishmentTargetShoesCurrent={details.replenishmentTargetShoesCurrent as any}
            storeReplenishment={storeReplenishment}
          />
        )

      case DETAILS_TYPE.LAST_DATA:
        return this.showLastData ? (
          <LastDataTab exposTimestamp={exposTimestamp} resaTimestamp={resaTimestamp} trafficTimestamp={trafficTimestamp} />
        ) : null

      default:
        return null
    }
  }

  render() {
    const { details, isSisterStore } = this.props
    if (!details) return null

    return (
      <div className="store-details-info">
        {isSisterStore && (
          <div className="modal-horizontal-padding store-details-info_name">
            <i className="icon compare-icon" />
            {details.storeName} {details.storeId}
          </div>
        )}
        <Box
          boxTitles={this.boxTitles()}
          updateStoreType={this.updateType}
          boxClassName={this.state.type === DETAILS_TYPE.RESULT ? 'hide-padding' : ''}
        >
          {this.renderContent()}
        </Box>
      </div>
    )
  }
}

export default withTranslation()(StoreDetailsInfo)
